import Decimal from 'decimal.js';
import { UnitOfMeasure } from '../UOM';
import {
  HiqLgmTypeCodeNames,
  RmaLgmTypeCode,
  RmaLgmTypeCodes,
} from './LgmTypeCodes';
import { HiqLrpTypeCodeNames } from '../lrp/LrpTypeCodes';

export const LgmTargetWeights = {
  LIVE_CATTLE: 'Target Live Cattle Weight',
  FEEDER_CATTLE: 'Target Feeder Cattle Weight',
  CORN: 'Target Corn Weight',
  LEAN_HOG: 'Target Lean Hog Weight',
  SOYBEAN_MEAL: 'Target Soybean Meal Weight',
} as const;

export type LgmTargetWeights =
  (typeof LgmTargetWeights)[keyof typeof LgmTargetWeights];

export type LgmCattleTargetWeightInfo = {
  min: Decimal;
  max: Decimal;
  default: Decimal;
  uom: UnitOfMeasure;
};

export type LgmSwineTargetWeightInfo = {
  default: Decimal;
  uom: UnitOfMeasure;
};

export const lgmSwineLeanHogFactor = new Decimal(0.74);

export const LgmCattleTargetWeightsByTypeCode: Record<
  | typeof RmaLgmTypeCodes.CALF_FINISHING
  | typeof RmaLgmTypeCodes.YEARLING_FINISHING,
  Record<'liveCattle' | 'feederCattle' | 'corn', LgmCattleTargetWeightInfo>
> = {
  [RmaLgmTypeCodes.CALF_FINISHING]: {
    liveCattle: {
      min: new Decimal(11),
      max: new Decimal(13),
      default: new Decimal(new Decimal(11.5)),
      uom: UnitOfMeasure.HundredWeight,
    },
    feederCattle: {
      min: new Decimal(4),
      max: new Decimal(6),
      default: new Decimal(5.5),
      uom: UnitOfMeasure.HundredWeight,
    },
    corn: {
      min: new Decimal(50),
      max: new Decimal(75),
      default: new Decimal(52),
      uom: UnitOfMeasure.Bushel,
    },
  },
  [RmaLgmTypeCodes.YEARLING_FINISHING]: {
    liveCattle: {
      min: new Decimal(12),
      max: new Decimal(15),
      default: new Decimal(12.5),
      uom: UnitOfMeasure.HundredWeight,
    },
    feederCattle: {
      min: new Decimal(6),
      max: new Decimal(9),
      default: new Decimal(7.5),
      uom: UnitOfMeasure.HundredWeight,
    },
    corn: {
      min: new Decimal(50),
      max: new Decimal(85),
      default: new Decimal(50),
      uom: UnitOfMeasure.Bushel,
    },
  },
};

export const LgmSwineTargetWeightsByCommodityTypeCode: Record<
  | typeof RmaLgmTypeCodes.FARROW_TO_FINISH
  | typeof RmaLgmTypeCodes.FINISHING
  | typeof RmaLgmTypeCodes.SEW_PIG_FINISHING,
  Record<'leanHog' | 'corn' | 'soybeanMeal', LgmSwineTargetWeightInfo>
> = {
  [RmaLgmTypeCodes.FARROW_TO_FINISH]: {
    leanHog: {
      default: new Decimal(2.6),
      uom: UnitOfMeasure.HundredWeight,
    },
    soybeanMeal: {
      default: new Decimal(0.069275),
      uom: UnitOfMeasure.ShortTon,
    },
    corn: {
      default: new Decimal(12),
      uom: UnitOfMeasure.Bushel,
    },
  },
  [RmaLgmTypeCodes.FINISHING]: {
    leanHog: {
      default: new Decimal(2.6),
      uom: UnitOfMeasure.HundredWeight,
    },
    soybeanMeal: {
      default: new Decimal(0.041),
      uom: UnitOfMeasure.ShortTon,
    },
    corn: {
      default: new Decimal(9),
      uom: UnitOfMeasure.Bushel,
    },
  },
  [RmaLgmTypeCodes.SEW_PIG_FINISHING]: {
    leanHog: {
      default: new Decimal(2.6),
      uom: UnitOfMeasure.HundredWeight,
    },
    soybeanMeal: {
      default: new Decimal(0.0455),
      uom: UnitOfMeasure.ShortTon,
    },
    corn: {
      default: new Decimal(9.05),
      uom: UnitOfMeasure.Bushel,
    },
  },
};

// See: LGM Cattle Handbook, Para 21, D(10) https://www.rma.usda.gov/-/media/RMA/Handbooks/Privately-Developed-Products---20000/Livestock-Gross-Margin-Cattle/2025-20060-LGM-Cattle-Insurance-Handbook.ashx
export const getLiveCattleTargetCwtPerHeadDefault = (
  rmaTypeCodeOrCommodityTypeName:
    | HiqLgmTypeCodeNames
    | HiqLrpTypeCodeNames
    | RmaLgmTypeCode
) => {
  // Default values for Calf Finishing, 807
  if (
    rmaTypeCodeOrCommodityTypeName === HiqLgmTypeCodeNames.CALF_FINISHING ||
    rmaTypeCodeOrCommodityTypeName === RmaLgmTypeCodes.CALF_FINISHING
  ) {
    return LgmCattleTargetWeightsByTypeCode[RmaLgmTypeCodes.CALF_FINISHING]
      ?.liveCattle?.default;
  }
  // Default values for Yearling Finishing, 808
  if (
    rmaTypeCodeOrCommodityTypeName === HiqLgmTypeCodeNames.YEARLING_FINISHING ||
    rmaTypeCodeOrCommodityTypeName === RmaLgmTypeCodes.YEARLING_FINISHING
  ) {
    return LgmCattleTargetWeightsByTypeCode[RmaLgmTypeCodes.YEARLING_FINISHING]
      ?.liveCattle?.default;
  }
  return null;
};

// See: LGM Cattle Handbook, Para 21, D(10) https://www.rma.usda.gov/-/media/RMA/Handbooks/Privately-Developed-Products---20000/Livestock-Gross-Margin-Cattle/2025-20060-LGM-Cattle-Insurance-Handbook.ashx
export const getFeederCattleTargetCwtPerHeadDefault = (
  commodityType: HiqLgmTypeCodeNames | HiqLrpTypeCodeNames | RmaLgmTypeCode
) => {
  // Default values for Calf Finishing, 807
  if (
    commodityType === HiqLgmTypeCodeNames.CALF_FINISHING ||
    commodityType === RmaLgmTypeCodes.CALF_FINISHING
  ) {
    return LgmCattleTargetWeightsByTypeCode[RmaLgmTypeCodes.CALF_FINISHING]
      .feederCattle.default;
  }
  // Default values for Yearling Finishing, 808
  if (
    commodityType === HiqLgmTypeCodeNames.YEARLING_FINISHING ||
    commodityType === RmaLgmTypeCodes.YEARLING_FINISHING
  ) {
    return LgmCattleTargetWeightsByTypeCode[RmaLgmTypeCodes.YEARLING_FINISHING]
      .feederCattle.default;
  }
  return null;
};

// See: LGM Cattle Handbook, Para 21, D(10) https://www.rma.usda.gov/-/media/RMA/Handbooks/Privately-Developed-Products---20000/Livestock-Gross-Margin-Cattle/2025-20060-LGM-Cattle-Insurance-Handbook.ashx
export const getCornTargetBuPerHeadDefault = (
  commodityType: HiqLgmTypeCodeNames | HiqLrpTypeCodeNames | RmaLgmTypeCode
) => {
  // Default values for Calf Finishing, 807
  if (
    commodityType === HiqLgmTypeCodeNames.CALF_FINISHING ||
    commodityType === RmaLgmTypeCodes.CALF_FINISHING
  ) {
    return LgmCattleTargetWeightsByTypeCode[RmaLgmTypeCodes.CALF_FINISHING].corn
      .default;
  }
  // Default values for Yearling Finishing, 808
  if (
    commodityType === HiqLgmTypeCodeNames.YEARLING_FINISHING ||
    commodityType === RmaLgmTypeCodes.YEARLING_FINISHING
  ) {
    return LgmCattleTargetWeightsByTypeCode[RmaLgmTypeCodes.YEARLING_FINISHING]
      .corn.default;
  }
  // Default values for Farrow to Finish, 804
  if (
    commodityType === HiqLgmTypeCodeNames.FARROW_TO_FINISH ||
    commodityType === RmaLgmTypeCodes.FARROW_TO_FINISH
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[
      RmaLgmTypeCodes.FARROW_TO_FINISH
    ].corn.default;
  }
  // Default values for Finishing Operation, 805
  if (
    commodityType === HiqLgmTypeCodeNames.FINISHING_OPERATION ||
    commodityType === RmaLgmTypeCodes.FINISHING
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[RmaLgmTypeCodes.FINISHING]
      .corn.default;
  }
  // Default values for Sew Pig Finishing, 806
  if (
    commodityType === HiqLgmTypeCodeNames.SEW_OPERATION ||
    commodityType === RmaLgmTypeCodes.SEW_PIG_FINISHING
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[
      RmaLgmTypeCodes.SEW_PIG_FINISHING
    ].corn.default;
  }
  return null;
};

export const getLeanHogTargetCwtPerHeadDefault = (
  commodityType: HiqLgmTypeCodeNames | HiqLrpTypeCodeNames | RmaLgmTypeCode
) => {
  // Default values for Farrow to Finish, 804
  if (
    commodityType === HiqLgmTypeCodeNames.FARROW_TO_FINISH ||
    commodityType === RmaLgmTypeCodes.FARROW_TO_FINISH
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[
      RmaLgmTypeCodes.FARROW_TO_FINISH
    ].leanHog.default;
  }
  // Default values for Finishing Operation, 805
  if (
    commodityType === HiqLgmTypeCodeNames.FINISHING_OPERATION ||
    commodityType === RmaLgmTypeCodes.FINISHING
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[RmaLgmTypeCodes.FINISHING]
      .leanHog.default;
  }
  // Default values for Sew Pig Finishing, 806
  if (
    commodityType === HiqLgmTypeCodeNames.SEW_OPERATION ||
    commodityType === RmaLgmTypeCodes.SEW_PIG_FINISHING
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[
      RmaLgmTypeCodes.SEW_PIG_FINISHING
    ].leanHog.default;
  }
  return null;
};

export const getSoybeanMealTargetTonPerHeadDefault = (
  commodityType: HiqLgmTypeCodeNames | HiqLrpTypeCodeNames | RmaLgmTypeCode
) => {
  // Default values for Farrow to Finish, 804
  if (
    commodityType === HiqLgmTypeCodeNames.FARROW_TO_FINISH ||
    commodityType === RmaLgmTypeCodes.FARROW_TO_FINISH
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[
      RmaLgmTypeCodes.FARROW_TO_FINISH
    ].soybeanMeal.default;
  }
  // Default values for Finishing Operation, 805
  if (
    commodityType === HiqLgmTypeCodeNames.FINISHING_OPERATION ||
    commodityType === RmaLgmTypeCodes.FINISHING
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[RmaLgmTypeCodes.FINISHING]
      .soybeanMeal.default;
  }
  // Default values for Sew Pig Finishing, 806
  if (
    commodityType === HiqLgmTypeCodeNames.SEW_OPERATION ||
    commodityType === RmaLgmTypeCodes.SEW_PIG_FINISHING
  ) {
    return LgmSwineTargetWeightsByCommodityTypeCode[
      RmaLgmTypeCodes.SEW_PIG_FINISHING
    ].soybeanMeal.default;
  }
  return null;
};
